import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { storage, database } from 'helpers/firebase';
import { getDateFromTsShort, getFileIcon, getSizeLabel } from 'helpers/functions';
import Paragraph from 'components/atoms/Paragraph';
import Loading from 'components/atoms/Loading';
import { fadeIn } from 'helpers/animations';

const StyledParagraph = styled(Paragraph)`
  max-width: 128px;
  word-wrap: break-word;
  border-radius: 5px;
  padding: 0 2px;
`;

const Wrapper = styled.button`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  margin-right: 10px;
  min-width: 64px;
  border-radius: 5px;
  padding: 5px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  align-self: stretch;
  outline: none;
  animation: ${fadeIn} ${({ theme }) => theme.fadeInDown} ${({ delay }) => delay}s;

  :hover,
  :focus {
    background-color: ${({ theme }) => theme.gray3};
  }

  :focus {
    ${StyledParagraph} {
      background-color: ${({ theme }) => theme.blue};
    }
  }
`;

const Icon = styled.div`
  height: 64px;
  width: 64px;
  margin-bottom: 5px;
`;

const Img = styled.img`
  height: 64px;
  width: auto;
  border-radius: 5px;
  margin-bottom: 5px;
`;

const InfoWrapper = styled.div`
  position: absolute;
  top: 0;
  left: calc(100% + 5px);
  padding: 10px;
  background-color: ${({ theme }) => theme.gray3};
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.16);
  border-radius: 10px;
  z-index: 99;
  transform-origin: top right;
  visibility: hidden;
`;

const InfoList = styled.ul`
  list-style-type: none;
`;

const InfoListItem = styled.li`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
`;

const Margin = styled.span`
  margin: 0 5px 0 0;
`;

function FileIcon({ name, type, id, folder, handleShowDetails, delay }) {
  const infoWrapperRef = useRef(null);
  const timerRef = useRef();

  const [url, setUrl] = useState(null);
  const [isHover, setIsHover] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (type.includes('image/')) {
      storage
        .ref(id)
        .getDownloadURL()
        .then((resultUrl) => {
          setUrl(resultUrl);
        });
    }
  }, [folder, id, type]);

  useEffect(() => {
    if (isHover) {
      timerRef.current = setTimeout(() => {
        if (data === null) {
          database
            .ref(id)
            .once('value')
            .then((snapshot) => {
              // eslint-disable-next-line
              const { name, type, size, lastModified, uploaded } = snapshot.val();
              const obj = { name, type };

              obj.size = getSizeLabel(size);
              obj.lastModified = getDateFromTsShort(lastModified);
              obj.uploaded = getDateFromTsShort(uploaded);

              setData(obj);
            });
        } else {
          const { width, x } = infoWrapperRef.current.getBoundingClientRect();
          const { innerWidth } = window;
          let dir = 1; // 0 = left, 1 = right

          if (width + x + 25 >= innerWidth) {
            dir = 0;
          }

          const tl = gsap.timeline({
            defaults: {
              duration: 0.3,
              ease: 'power2.out',
            },
          });

          tl.set(infoWrapperRef.current.children, { autoAlpha: 0 });

          if (!dir) {
            tl.set(infoWrapperRef.current, { left: `${-width - 5}px` });
          } else {
            tl.set(infoWrapperRef.current, { left: `calc(100% + 5px)` });
          }

          tl.fromTo(
            infoWrapperRef.current,
            { width: 0, height: '32px' },
            { width: 'auto', height: '32px', autoAlpha: 1 },
          )
            .to(infoWrapperRef.current, {
              height: 'auto',
            })
            .to(infoWrapperRef.current.children, {
              stagger: 0.1,
              autoAlpha: 1,
            });
        }
      }, 250);
    } else {
      clearTimeout(timerRef.current);
      gsap.to(infoWrapperRef.current, {
        autoAlpha: 0,
        duration: 0.3,
        ease: 'power2.out',
        delay: 0.2,
      });
    }

    // eslint-disable-next-line
  }, [isHover, data]);

  return (
    <Wrapper
      type="button"
      onClick={() => handleShowDetails(id)}
      delay={delay}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      {url ? <Img src={url} alt="Ikona" /> : <Icon>{getFileIcon(type)}</Icon>}
      <StyledParagraph>{name}</StyledParagraph>
      <InfoWrapper ref={infoWrapperRef}>
        {data !== null ? (
          <InfoList>
            <InfoListItem>
              <Paragraph secondary>Rozmiar</Paragraph>
              <Margin />
              <Paragraph>{data.size}</Paragraph>
            </InfoListItem>
            <InfoListItem>
              <Paragraph secondary>Edytowano</Paragraph>
              <Margin />
              <Paragraph>{data.lastModified}</Paragraph>
            </InfoListItem>
            <InfoListItem>
              <Paragraph secondary>Dodano</Paragraph>
              <Margin />
              <Paragraph>{data.uploaded}</Paragraph>
            </InfoListItem>
          </InfoList>
        ) : (
          <Loading small />
        )}
      </InfoWrapper>
    </Wrapper>
  );
}

FileIcon.propTypes = {
  name: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  folder: PropTypes.string,
  handleShowDetails: PropTypes.func.isRequired,
  delay: PropTypes.number,
};

FileIcon.defaultProps = {
  folder: null,
  delay: 1,
};

export default FileIcon;
